import React, { Children, useEffect } from 'react';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';

// Tracking
import { trackFlowStepCompleted } from '@zola/tracking-contracts/src/tracking';
import { FlowStepCompleted } from '@zola/tracking-contracts/src/events';

import { StyledBackLink, StepCount } from './OnboardSection.styles';

import { useOnboardingContext } from '../context';
import type { GenericOnboardingRequest, GenericOnboaringResponse } from '../types';

const FORM_ERROR = 'FINAL_FORM/form-error';

export type OnboardSectionProps = {
  showStepCount?: boolean;
  className?: string;
  onFormSubmit: (values: GenericOnboardingRequest) => Promise<GenericOnboaringResponse>;
  formInitialValues?: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
};

const OnboardSection: React.FC<OnboardSectionProps> = ({
  className,
  showStepCount,
  children,
  onFormSubmit,
  formInitialValues,
}): JSX.Element => {
  const { onboardingState, setOnboardingState, flowTrackingContractsData } = useOnboardingContext();
  const {
    section,
    step,
    business_unit_source,
    business_unit_feature,
    sectionsLength,
    absoluteStepIndex,
    currentUserContext,
  } = onboardingState;
  const allSteps = Children.toArray(children);
  const stepsLength = allSteps.length;
  const activeStep = allSteps[step];
  const isLast = step === stepsLength - 1;
  const isExistingUser = !currentUserContext.is_guest;

  useEffect(() => {
    if (stepsLength !== onboardingState.sectionStepsLength)
      setOnboardingState({ ...onboardingState, sectionStepsLength: stepsLength });
  }, [stepsLength, onboardingState, setOnboardingState]);

  const handleBack = () => {
    setOnboardingState({
      ...onboardingState,
      step: step - 1,
      absoluteStepIndex: absoluteStepIndex - 1,
    });
  };

  const handleSubmitForm = (values: Record<string, string | number | boolean>) => {
    if (isLast) {
      // Engineers should attach .catch() to their onFormSubmit from parent component
      // to handle errors individually for each API call; adding a .catch here ultimately swallows message
      return onFormSubmit(values)
        .then(() => {
          const flowEventContext: FlowStepCompleted = {
            ...flowTrackingContractsData,
            step_number: absoluteStepIndex + 1,
          };
          trackFlowStepCompleted(flowEventContext);

          const isLastSection = section === sectionsLength - 1;
          if (isLastSection) {
            setOnboardingState({ ...onboardingState, onboardingCompleted: true });
          } else {
            setOnboardingState({
              ...onboardingState,
              section: section + 1,
              sectionStepsLength: 0,
              step: 0,
              absoluteStepIndex: absoluteStepIndex + 1,
            });
          }
        })
        .catch(() => {
          return { [FORM_ERROR]: 'Submission Error' };
        });
    }

    const flowEventContext: FlowStepCompleted = {
      ...flowTrackingContractsData,
      step_number: absoluteStepIndex + 1,
    };
    trackFlowStepCompleted(flowEventContext);

    setOnboardingState({
      ...onboardingState,
      step: step + 1,
      absoluteStepIndex: absoluteStepIndex + 1,
    });

    return Promise.resolve();
  };

  return (
    <div data-testid="OnboardSection" className={className}>
      {allSteps.length > 1 && step > 0 && (
        <StyledBackLink arrow arrowPosition="prefix" role="button" onClick={handleBack}>
          Back
        </StyledBackLink>
      )}
      {showStepCount && (
        <StepCount>
          Step {step + 1} of {allSteps.length}
        </StepCount>
      )}
      <FinalForm
        onSubmit={handleSubmitForm}
        initialValues={{
          business_unit_feature,
          business_unit_source,
          existing_user: isExistingUser,
          ...formInitialValues,
        }}
      >
        {({ handleSubmit }: FormRenderProps): JSX.Element => (
          <form
            onSubmit={(e) => {
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              handleSubmit(e);
            }}
          >
            {activeStep}
          </form>
        )}
      </FinalForm>
    </div>
  );
};

export default OnboardSection;
